import { mapUnitToCompanyCurrency } from 'containers/Esrs/utils';
import { EsrsReportMetric } from '../../Report.types';
import { Bookmark, Paragraph, TableCell, TableRow, TextRun, VerticalAlign } from 'docx';
import { TextStyle } from '../reportThemes';
import { getRowTitle, getTableRowText } from '../report-helper-functions';
import { AggregatedMetricsTableData } from 'containers/Esrs';
import { TableData } from 'Molecules/NestedTable';

const createRowIdentifier = (row: TableData<AggregatedMetricsTableData>): string => {
  const reference = row.metric.reference;
  const tagName = row.subRows?.length && row.tagName ? row.tagName : null;
  const tagValues = row.tags?.length ? row.tags.map((t) => t.tagValue).join('-') : null;

  return [reference, tagName, tagValues].filter(Boolean).join('-');
};

export const createNestedDataPointsRows = (
  metricResults: AggregatedMetricsTableData,
  companyCurrency: string,
  uniqueRowRefs: string[],
  depth = 1
) => {
  const subRows = metricResults.subRows;

  if (!subRows?.length) return [];

  const isUmbrellaMetric = metricResults.metric.calculation === 'NONE';

  const isTagWithChildMetrics =
    metricResults.metric.childrenMetrics?.length && metricResults.tagName;

  const withChildrenAndTags = isTagWithChildMetrics && metricResults.tagType;

  // Case for tag with child metric subRows => show only the tag
  if (isTagWithChildMetrics && !isUmbrellaMetric && !withChildrenAndTags) return [];

  const nestedRows: TableRow[] =
    subRows?.flatMap((row) => {
      const uniqueIdentifier = createRowIdentifier(row);
      const alreadyExists = uniqueRowRefs.includes(uniqueIdentifier);
      if (!alreadyExists) {
        uniqueRowRefs.push(uniqueIdentifier);
      }

      return [
        new TableRow({
          children: [
            new TableCell({
              verticalAlign: VerticalAlign.TOP,
              children: [
                new Paragraph({
                  indent: {
                    start: depth * 220,
                  },
                  children: [
                    new Bookmark({
                      id: row.metric.reference,
                      children: [
                        new TextRun({
                          text: getRowTitle(row, metricResults, companyCurrency),
                        }),
                      ],
                    }),
                  ],
                  style: TextStyle.body,
                }),
              ],
            }),
            new TableCell({
              verticalAlign: VerticalAlign.TOP,
              children: [
                new Paragraph({
                  text: getTableRowText(row),
                  style: TextStyle.body,
                }),
              ],
            }),
          ],
        }),
        ...(alreadyExists
          ? []
          : createNestedDataPointsRows(row, companyCurrency, uniqueRowRefs, depth + 1)),
      ];
    }) ?? [];

  return nestedRows.flat();
};
export const createDataPointRows = (
  metric: EsrsReportMetric,
  companyCurrency: string,
  uniqueRowRefs: string[]
) => {
  const metricResults = metric.tableData;
  const metricInfo = metric.metric.metric;
  const metricTitle = metricInfo.shortTitle ?? metricInfo.title;
  const unitOfMeasurement = metricInfo.unitOfMeasurement
    ? ` (${mapUnitToCompanyCurrency(metricInfo.unitOfMeasurement, companyCurrency)})`
    : '';
  const hasCalculation = metricInfo.calculation !== '' && metricInfo.calculation !== null;

  const withChildrenAndTags =
    metric.metric.childrenMetrics?.length > 0 && !!metricResults.subRows?.[0].tagName;

  // Umbrella metrics are metrics with calculation set to "NONE"
  const isUmbrellaMetric = metricInfo.calculation === 'NONE';

  const uniqueRowIdentifier = createRowIdentifier(metricResults);
  const alreadyExists = uniqueRowRefs.includes(uniqueRowIdentifier);
  if (!alreadyExists) {
    uniqueRowRefs.push(uniqueRowIdentifier);
  }

  const displayedRows = [
    !isUmbrellaMetric
      ? new TableRow({
          children: [
            new TableCell({
              verticalAlign: VerticalAlign.TOP,
              children: [
                new Paragraph({
                  children: [
                    new Bookmark({
                      id: metricInfo.reference,
                      children: [
                        new TextRun({
                          text: `${metricTitle}${unitOfMeasurement}`,
                        }),
                      ],
                    }),
                  ],
                  style: TextStyle.body,
                }),
              ],
            }),
            new TableCell({
              verticalAlign: VerticalAlign.TOP,
              children: [
                new Paragraph({
                  text: getTableRowText(metric.tableData),
                  style: TextStyle.body,
                }),
              ],
            }),
          ],
        })
      : [],
    // If the metric has calculation, we do not display nested rows
    hasCalculation && !withChildrenAndTags && !isUmbrellaMetric
      ? []
      : alreadyExists
        ? []
        : createNestedDataPointsRows(metricResults, companyCurrency, uniqueRowRefs),
  ];
  return displayedRows.flat();
};
